import { Box, Typography } from '@mui/material';
import { ProjectLink } from 'app/common/components/link';
import theme from 'app/common/materialTheme';
import type { ImmutableMap } from 'app/common/utils/immutable-types';
import type { Member } from 'app/project/types';
import type { List as ImmutableList } from 'immutable';
import type React from 'react';
import { PureComponent } from 'react';

export type DelinquencyBannerProps = {
  readonly billingStatus?: string;
  readonly hasBillingPermissions?: boolean;
  readonly admins?: ImmutableList<ImmutableMap<Member>>;
  readonly getProjectAdmins: () => void;
};

export default class DelinquencyBanner extends PureComponent<DelinquencyBannerProps> {
  override render() {
    const { billingStatus, hasBillingPermissions, admins } = this.props;

    let message: React.ReactNode;
    if (hasBillingPermissions && billingStatus) {
      if (['unpaid', 'canceled', 'past_due'].includes(billingStatus)) {
        message = (
          <Typography>
            Your payment is past due. Please{' '}
            <ProjectLink
              href="{projectPath}/billing"
              style={{
                color: theme.palette.error.contrastText,
                textDecoration: 'underline',
                fontWeight: 'bold',
                '&::after': {
                  backgroundBolor: 'transparent'
                }
              }}
            >
              enter a valid payment method or contact support
            </ProjectLink>{' '}
            so that service is not interrupted.
          </Typography>
        );
      }
    } else if (billingStatus === 'past_due') {
      const adminsJs = admins?.toArray();
      message = (
        <Typography>
          Payment for this project is past due, and it may be frozen unless resolved.
          {admins !== undefined && (
            <>
              {' '}
              Please contact a project administrator to resolve this issue. Your project administrators are:
              {adminsJs?.map((admin) => {
                const adminEmail = admin?.get('email');
                return adminEmail ? (
                  <Box
                    key={adminEmail}
                    component="span"
                    sx={{
                      '&:first-child::before': {
                        content: ' '
                      },
                      '&::before': {
                        content: ', '
                      }
                    }}
                  >
                    {admin?.get('fullName')} (<a href={`mailto:${adminEmail}`}>{adminEmail}</a>)
                  </Box>
                ) : null;
              })}
            </>
          )}
        </Typography>
      );
    }

    if (message) {
      return (
        <Box
          sx={{
            backgroundColor: 'error.main',
            color: 'error.contrastText',
            padding: 1,
            marginTop: 6,
            marginBottom: -6
          }}
        >
          {message}
        </Box>
      );
    }

    return null;
  }
}
